import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { DndProvider, useDrag, useDrop, useDragLayer } from "react-dnd";
import { HTML5Backend, getEmptyImage } from "react-dnd-html5-backend";
import { toast } from "react-toastify";
import pdfDetails from "../../../api/pdfDetails";
import combineDocuments from "../../../api/combineDocuments";
import { renderAsync } from "docx-preview";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const ItemType = "DOCUMENT_ITEM";

const getFileExt = (url = "") => {
  if (!url) return "";
  const clean = url.split("?")[0].toLowerCase();
  const parts = clean.split(".");
  return parts[parts.length - 1] || "";
};

const PdfPreview = ({ url, small }) => {
  const width = small ? 120 : 240;
  const height = small ? 160 : 320;
  const pdfSrc = `${url}#page=1&view=FitH`;
  const gview = `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(
    url,
  )}`;

  return (
    <div
      style={{
        width,
        height,
        borderRadius: 6,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid #ddd",
        pointerEvents: "none",
      }}
    >
      <object data={pdfSrc} type="application/pdf" width="100%" height="100%">
        <iframe
          title="PDF preview"
          src={gview}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </object>
    </div>
  );
};

/**
 * DraggableItem
 *
 * fromList === "available" -> left panel item. Now supports:
 *   - checkbox selection (checked / onToggleCheck / selectionOrder)
 *   - still draggable onto the right panel (onDrop) for users who prefer drag/drop
 *   - dims/blurs once `moved` is true (i.e. it has already been placed on the right)
 *
 * fromList === "selected" -> right panel item, draggable to reorder via moveItem.
 */
const DraggableItem = ({
  page,
  fromList,
  onDrop,
  index,
  moveItem,
  checked,
  onToggleCheck,
  selectionOrder,
  moved,
}) => {
  const [{ isDragging }, dragRef, preview] = useDrag({
    type: ItemType,
    item: { page, index, fromList },
    canDrag: !moved,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    // hide the default browser drag image; we'll use CustomDragLayer
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const [, dropRef] = useDrop({
    accept: ItemType,
    hover: (item) => {
      if (
        item.fromList === "selected" &&
        typeof index === "number" &&
        index !== item.index
      ) {
        moveItem(item.index, index);
        item.index = index;
      }
    },
    drop: (item) => {
      if (item.fromList === "available" && onDrop) {
        onDrop(item.page);
      }
    },
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const wordPreviewRef = useRef(null);

  // Load preview based on file type (image / docx)
  useEffect(() => {
    setImagePreview(null);
    setLoading(false);

    if (wordPreviewRef.current) {
      wordPreviewRef.current.innerHTML = "";
    }

    if (!page?.url) return;

    const ext = getFileExt(page.url);

    if (["jpg", "jpeg", "png"].includes(ext)) {
      setImagePreview(page.url);
      return;
    }

    if (ext === "docx") {
      let cancelled = false;

      const loadDocx = async () => {
        setLoading(true);
        try {
          const arrayBuffer = await fetch(page.url).then((res) =>
            res.arrayBuffer(),
          );
          if (!wordPreviewRef.current || cancelled) return;

          // Render without wrapper and without fixed page width/height
          await renderAsync(arrayBuffer, wordPreviewRef.current, null, {
            inWrapper: false,
            ignoreWidth: true,
            ignoreHeight: true,
            breakPages: false,
          });

          if (!wordPreviewRef.current) return;

          // Just in case wrapper/section are still there from styles,
          // strip padding/background so nothing is cropped.
          const wrapper = wordPreviewRef.current.querySelector(".docx-wrapper");
          if (wrapper) {
            wrapper.style.padding = "0";
            wrapper.style.margin = "0";
            wrapper.style.background = "transparent";
            wrapper.style.width = "100%";
            wrapper.style.boxSizing = "border-box";
          }

          const sections =
            wordPreviewRef.current.querySelectorAll("section.docx");
          sections.forEach((section) => {
            section.style.padding = "0";
            section.style.margin = "0";
            section.style.width = "100%";
            section.style.minHeight = "auto";
            section.style.background = "transparent";
            section.style.boxSizing = "border-box";
          });
          // Apply horizontal scroll to the article tag
          const article = wordPreviewRef.current.querySelector("article");
          if (article) {
            article.style.overflowX = "auto";
          }
        } catch (err) {
          console.error("Error loading Word document:", err);
          if (wordPreviewRef.current) {
            wordPreviewRef.current.innerHTML =
              "<p>Preview not available. Error loading Word document.</p>";
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      loadDocx();

      return () => {
        cancelled = true;
      };
    }
  }, [page]);

  const renderPreviewContent = () => {
    const ext = getFileExt(page?.url);

    if (ext === "pdf") {
      return <PdfPreview url={page.url} small={fromList === "selected"} />;
    }

    if (imagePreview) {
      const THUMB_WIDTH = 150;
      const PAGE_RATIO = 1.414;
      const VISIBLE_HEIGHT = Math.round(THUMB_WIDTH * PAGE_RATIO);

      return (
        <div
          style={{
            width: THUMB_WIDTH,
            height: VISIBLE_HEIGHT,
            borderRadius: 6,
            overflow: "hidden",
            background: "#fff",
            border: "1px solid #ddd",
          }}
        >
          <img
            src={imagePreview}
            alt={page.name}
            width={THUMB_WIDTH}
            style={{ display: "block", height: "auto" }}
          />
        </div>
      );
    }

    if (ext === "docx") {
      return (
        <>
          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: 8,
              }}
            >
              <CircularProgress size={20} />
            </div>
          )}
          <div
            ref={wordPreviewRef}
            style={{
              maxHeight: 230,
              overflow: "auto",
              margin: 0,
              padding: 0,
              boxSizing: "border-box",
              background: "transparent",
            }}
          />
        </>
      );
    }

    if (ext === "doc") {
      // Using Google Docs Viewer for .doc files
      const gviewUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
        page.url,
      )}`;
      return (
        <iframe
          src={gviewUrl}
          width="100%"
          height="230px"
          style={{
            border: "1px solid #ddd",
            borderRadius: "6px",
          }}
          title="Google Docs Viewer"
        />
      );
    }

    return (
      <Typography variant="body2" color="textSecondary">
        Preview not available
      </Typography>
    );
  };

  const isAvailableList = fromList === "available";

  return (
    <div
      ref={(node) => dropRef(dragRef(node))}
      style={{
        border: "1px solid #ccc",
        padding: 8,
        marginBottom: 10,
        borderRadius: 8,
        backgroundColor: isDragging ? "#eef6ff" : "#f9f9f9",
        width: "100%",
        maxWidth: fromList === "selected" ? 240 : "100%",
        userSelect: "none",
        cursor: moved ? "default" : "grab",
        opacity: isDragging ? 0 : moved ? 0.45 : 1,
        filter: moved ? "blur(1.5px)" : "none",
        pointerEvents: moved ? "none" : "auto",
        position: "relative",
        transition: "opacity 0.2s ease, filter 0.2s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        {isAvailableList && (
          <Checkbox
            size="small"
            checked={!!checked}
            onChange={() => onToggleCheck && onToggleCheck(page)}
            disabled={moved}
            sx={{ p: 0.5 }}
          />
        )}
        <IconButton
          ref={dragRef}
          size="small"
          aria-label="Drag"
          title="Drag to move"
          sx={{ cursor: moved ? "default" : "grab" }}
          disabled={moved}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle2"
          noWrap
          title={page.name}
          sx={{ flex: 1 }}
        >
          {page.name}
        </Typography>
        {isAvailableList && selectionOrder ? (
          <Typography
            variant="caption"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {selectionOrder}
          </Typography>
        ) : null}
      </div>
      {renderPreviewContent()}
    </div>
  );
};

const CustomDragLayer = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) return null;

  const style = {
    position: "fixed",
    pointerEvents: "none",
    left: currentOffset.x,
    top: currentOffset.y,
    zIndex: 1301,
    width: 240,
    opacity: 1,
  };

  return (
    <div style={style}>
      <div
        style={{
          padding: 8,
          border: "1px solid #ccc",
          borderRadius: 8,
          background: "#f9f9f9",
        }}
      >
        <Typography variant="subtitle2" noWrap>
          {item.page?.name || "Dragging"}
        </Typography>
        {item.page?.url?.endsWith(".pdf") ? (
          <PdfPreview url={item.page.url} small />
        ) : item.page?.url &&
          [".jpg", ".jpeg", ".png"].some((ext) =>
            item.page.url.toLowerCase().endsWith(ext),
          ) ? (
          (() => {
            const THUMB_WIDTH = 120;
            const PAGE_RATIO = 1.414;
            const VISIBLE_HEIGHT = Math.round(THUMB_WIDTH * PAGE_RATIO);
            return (
              <div
                style={{
                  width: THUMB_WIDTH,
                  height: VISIBLE_HEIGHT,
                  borderRadius: 6,
                  overflow: "hidden",
                  background: "#fff",
                  border: "1px solid #ddd",
                }}
              >
                <img
                  src={item.page.url}
                  alt={item.page.name}
                  width={THUMB_WIDTH}
                  style={{ display: "block", height: "auto" }}
                />
              </div>
            );
          })()
        ) : item.page?.url &&
          (item.page.url.endsWith(".docx") ||
            item.page.url.endsWith(".doc")) ? (
          <Typography variant="body2" color="textSecondary">
            Word Preview
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Preview not available
          </Typography>
        )}
      </div>
    </div>
  );
};

const DropZone = ({ onDrop, children }) => {
  const [, dropRef] = useDrop({
    accept: ItemType,
    drop: (item) => {
      if (item.fromList === "available") onDrop(item.page);
    },
  });

  return (
    <div
      ref={dropRef}
      style={{
        height: "100%",
        border: "2px dashed #999",
        borderRadius: "8px",
        padding: "10px",
        backgroundColor: "#fafafa",
        overflowY: "auto",
      }}
    >
      {children}
    </div>
  );
};

const CombineDocumentsPopup = ({
  open,
  setOpen,
  selectedDocuments = [],
  token,
  document_table,
  setDocumentUpdatedToTrue,
}) => {
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [isCombining, setIsCombining] = useState(false);

  // checkedIds preserves the ORDER the user clicked checkboxes in (Map insertion order).
  // This drives combine order when "Move" is used instead of drag/drop or Select All.
  const [checkedIds, setCheckedIds] = useState([]); // array of document_id, in click order
  const [selectAll, setSelectAll] = useState(false);

  // document_ids that have already been moved to the right panel — used to blur them on the left
  const movedIds = new Set(selectedPages.map((p) => p.document_id));

  useEffect(() => {
    const fetchPdfLinks = async () => {
      if (!open || selectedDocuments.length === 0) return;
      try {
        const results = await Promise.all(
          selectedDocuments.map(async (doc) => {
            const docId = doc.documentID || doc;
            const response = await pdfDetails(token, docId, document_table);
            const data = response?.data;
            if (response.code === 200 && data?.[0]?.pdflink) {
              return {
                document_id: docId,
                name: doc.document_name || `Document ${docId}`,
                url: data[0].pdflink,
              };
            } else {
              toast.error("Failed to fetch document", {
                position: "top-center",
                autoClose: 5000,
                theme: "dark",
              });
              return null;
            }
          }),
        );
        const validResults = results.filter(Boolean);
        setAvailablePages(validResults);
      } catch (err) {
        console.error("Error fetching document details:", err);
        toast.error("An error occurred while loading documents", {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
        });
      }
    };

    fetchPdfLinks();
    // Reset selection state whenever the dialog (re)opens
    setSelectedPages([]);
    setCheckedIds([]);
    setSelectAll(false);
  }, [open, selectedDocuments, token, document_table]);

  const moveItem = (fromIdx, toIdx) => {
    const updated = [...selectedPages];
    const [movedItem] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, movedItem);
    setSelectedPages(updated);
  };

  const addToSelected = (page) => {
    if (!selectedPages.find((p) => p.document_id === page.document_id)) {
      setSelectedPages((prev) => [...prev, page]);
    }
    // Clean up checkbox state for anything dragged directly over
    setCheckedIds((prev) => prev.filter((id) => id !== page.document_id));
  };

  // Toggle an individual checkbox. Order of checking is preserved in checkedIds.
  const handleToggleCheck = (page) => {
    setCheckedIds((prev) => {
      if (prev.includes(page.document_id)) {
        return prev.filter((id) => id !== page.document_id);
      }
      return [...prev, page.document_id];
    });
  };

  // Select All toggles every still-available (not yet moved) document.
  // When checked, order follows the natural list order of availablePages.
  const handleSelectAllToggle = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allAvailableIds = availablePages
        .filter((p) => !movedIds.has(p.document_id))
        .map((p) => p.document_id);
      setCheckedIds(allAvailableIds);
    } else {
      setCheckedIds([]);
    }
  };

  // "Move" button: pushes all checked documents to the right panel,
  // in the order they were checked (or natural order if Select All was used).
  const handleMoveClick = () => {
    if (checkedIds.length === 0) {
      toast.error("Please select at least one document to move", {
        position: "top-center",
        autoClose: 4000,
        theme: "dark",
      });
      return;
    }

    const idToPage = new Map(availablePages.map((p) => [p.document_id, p]));
    const toMove = checkedIds
      .map((id) => idToPage.get(id))
      .filter(Boolean)
      .filter((p) => !movedIds.has(p.document_id));

    setSelectedPages((prev) => [...prev, ...toMove]);
    setCheckedIds([]);
    setSelectAll(false);
  };

  const handleClose = () => setOpen(false);

  const handleCombine = async () => {
    if (selectedPages.length < 2) {
      toast.error("Please select at least two documents to combine", {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
      });
      return;
    }

    const document_ids = Array.from(
      new Set(selectedPages.map((p) => p?.document_id).filter(Boolean)),
    );

    if (document_ids.length < 2) {
      toast.error("Please select at least two valid documents", {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
      });
      return;
    }

    const extFromUrl = (url) => {
      if (!url) return null;
      return getFileExt(url);
    };

    const kindFromExt = (ext) => {
      if (ext === "pdf") return "pdf";
      if (ext === "doc" || ext === "docx") return "word";
      if (["jpeg", "jpg", "png"].includes(ext)) return "images";
      return null;
    };

    const kinds = Array.from(
      new Set(
        selectedPages
          .map((p, i) => {
            const ext = extFromUrl(p?.url);
            if (!ext) {
              toast.error(
                `Could not detect file type for document #${i + 1}.`,
                {
                  position: "top-center",
                  autoClose: 5000,
                  theme: "dark",
                },
              );
              return null;
            }
            const k = kindFromExt(ext);
            if (!k) {
              toast.error(
                `Unsupported file type ".${ext}" for document #${
                  i + 1
                }. Use PDF / Word / Images.`,
                {
                  position: "top-center",
                  autoClose: 5000,
                  theme: "dark",
                },
              );
            }
            return k;
          })
          .filter(Boolean),
      ),
    );

    if (kinds.length === 0) return;

    if (kinds.length > 1) {
      toast.error(
        "Please select documents of the same type (all PDFs, all Word, or all Images).",
        {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
        },
      );
      return;
    }

    const doc_type = kinds[0];

    try {
      setIsCombining(true);

      const response = await combineDocuments({
        token,
        document_ids,
        doc_type,
      });

      if (response?.code === 200) {
        toast.success("Documents combined successfully", {
          position: "top-center",
          autoClose: 3000,
          theme: "dark",
        });
        setOpen(false);
        setDocumentUpdatedToTrue();
      } else {
        toast.error("Failed to combine documents", {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
        });
      }
    } catch (error) {
      console.error("Combine API failed:", error);
      toast.error("Failed to combine documents", {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
      });
    } finally {
      setIsCombining(false);
    }
  };

  const availableCount = availablePages.filter(
    (p) => !movedIds.has(p.document_id),
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DndProvider backend={HTML5Backend}>
        <DialogTitle>Drag Documents to Combine</DialogTitle>
        <DialogContent>
          <CustomDragLayer />
          <div style={{ height: "600px" }}>
            <Grid container spacing={3} style={{ height: "100%" }}>
              <Grid item xs={6} style={{ height: "100%", overflowY: "auto" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0 }}
                >
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    All Documents
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleMoveClick}
                    disabled={checkedIds.length === 0}
                  >
                    Move
                  </Button>
                </Stack>
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectAll}
                      indeterminate={
                        !selectAll &&
                        checkedIds.length > 0 &&
                        checkedIds.length < availableCount
                      }
                      onChange={handleSelectAllToggle}
                      disabled={availableCount === 0}
                    />
                  }
                  label={<Typography variant="body2">Select All</Typography>}
                />
                {availablePages.map((page) => (
                  <DraggableItem
                    key={page.document_id}
                    page={page}
                    fromList="available"
                    onDrop={addToSelected}
                    checked={checkedIds.includes(page.document_id)}
                    onToggleCheck={handleToggleCheck}
                    selectionOrder={
                      checkedIds.includes(page.document_id)
                        ? checkedIds.indexOf(page.document_id) + 1
                        : null
                    }
                    moved={movedIds.has(page.document_id)}
                  />
                ))}
              </Grid>
              <Grid item xs={6} style={{ height: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Selected for Combine
                </Typography>
                <DropZone onDrop={addToSelected}>
                  {selectedPages.length === 0 ? (
                    <Typography color="textSecondary">
                      Drop documents here, or check items on the left and click
                      "Move"
                    </Typography>
                  ) : (
                    selectedPages.map((page, index) => (
                      <DraggableItem
                        key={page.document_id}
                        page={page}
                        index={index}
                        fromList="selected"
                        moveItem={moveItem}
                      />
                    ))
                  )}
                </DropZone>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCombine}
            disabled={selectedPages.length < 2 || isCombining}
            startIcon={isCombining ? <CircularProgress size={20} /> : null}
          >
            Combine
          </Button>
        </DialogActions>
      </DndProvider>
    </Dialog>
  );
};

export default CombineDocumentsPopup;
